import { useState, useEffect, useCallback, useRef } from "react";
import type { SignupEmailNotice } from "../types/auth";

export interface OnboardingData {
  // Auth credentials
  authMethod: "google" | "apple" | null;
  googleCredential: string | null;
  appleCredential: { idToken: string; user?: { name?: string; email?: string } } | null;

  // Step 2: Profile — name, photo and Instagram on one screen (founder,
  // 1.5.1 #1). Venue and bio are no longer asked at sign-up; they stay in
  // Edit profile.
  name: string;
  email: string;
  slug: string;
  instagram: string;

  // Profile photo (captured on the profile step)
  avatarFile: File | null;
  avatarPreview: string | null;
  // Phase C: price + currency left the wizard entirely — the server derives
  // the currency from the bank country and the DJ picks a price in the
  // first-sign-in popup (components/PriceSetupModal.tsx).

  // Bank country for Stripe
  bankCountryName: string;

  // Refer & Earn: the code of the talent who invited them (optional). From a
  // /join link or typed on the profile step; the server ignores a wrong one.
  referralCode: string;

  // Connect Instagram, its own step before the bank (build 54, founder's
  // option B). igStep: the sign-in check said it is on for this sign-up (both
  // Instagram switches); then the profile step has no Instagram box. igClaim:
  // the claim token the server handed back, sent with register.
  // igUsername: the handle Instagram vouched for ("" = not connected).
  igStep: boolean;
  igClaim: string;
  igUsername: string;

  // App-tips emails: the sign-in screen shows ONE of two boxes, by the phone's
  // timezone, or none where the law needs no choice (Step1Welcome). Each box
  // keeps its own tick, so a box swapped in after the screen opened never
  // inherits the other one's.
  //   "Don't email me app tips" (Europe, the UK, most countries): ticked = they refused.
  emailTipsOptOut: boolean;
  //   "Email me app tips" (where the law wants an explicit yes): ticked = yes.
  emailTipsOptIn: boolean;
  // Which box they saw, by its wording's version, stamped when they leave the
  // sign-in screen through Google or Apple. null = they saw none (none there,
  // they left before it came, or a sign-up saved by an older build): the
  // register call then sends none of these, and the server's country rules
  // decide.
  emailNotice: SignupEmailNotice | null;
}

// Step ids keep their meaning across builds (a saved draft resumes on the
// right screen): 1 sign-in, 2 profile, 3 bank — and 4, Connect Instagram,
// which sits BETWEEN profile and bank, only when the sign-up has it.
export const IG_STEP = 4;

// v4 (founder, 1.5.1 #1): the two profile screens became one, so the bank
// step moved from 4 to 3. A draft saved under an older key would resume on
// the wrong step — versioning the key resets in-flight drafts once, cleanly.
// (v3 was build 41, when the access-code interstitial left; v2 the Phase C
// renumber, when the price step left the wizard.)
const STORAGE_KEY = "onboarding_state_v4";

const initialState: OnboardingData = {
  authMethod: null,
  googleCredential: null,
  appleCredential: null,
  name: "",
  email: "",
  slug: "",
  instagram: "",
  avatarFile: null,
  avatarPreview: null,
  bankCountryName: "",
  referralCode: "",
  igStep: false,
  igClaim: "",
  igUsername: "",
  emailTipsOptOut: false,
  emailTipsOptIn: false,
  emailNotice: null,
};

// Read prior onboarding state synchronously so React's first render already
// has the right step/data. A useEffect-based load races with the save effect
// under React Strict Mode (dev) — save runs first with the default state and
// overwrites the persisted value before load can apply it, dumping the user
// back to step 1 on mid-onboarding refresh.
function readPersistedState(): { step: number; data: OnboardingData } {
  if (typeof window === "undefined") return { step: 1, data: initialState };
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (!saved) return { step: 1, data: initialState };
    const parsed = JSON.parse(saved);
    const data: OnboardingData = {
      ...initialState,
      ...(parsed.data || {}),
      avatarFile: null, // File objects don't serialize
    };
    const step = typeof parsed.step === "number" ? parsed.step : 1;
    // The Instagram step only exists for a sign-up that has it.
    return { step: step === IG_STEP && !data.igStep ? 3 : step, data };
  } catch {
    return { step: 1, data: initialState };
  }
}

/** A sign-up waiting on Instagram's sign-in (its saved draft holds a claim):
    where a return that lost its to=signup belongs (pages/InstagramCallbackPage). */
export function hasPendingInstagramSignup(): boolean {
  try {
    const d = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "null")?.data;
    return !!(d && d.igStep && d.igClaim);
  } catch {
    return false;
  }
}

export const stepOrder = (igStep: boolean): number[] => (igStep ? [1, 2, IG_STEP, 3] : [1, 2, 3]);

/** Where a step sits on the "Step N of M" strip (profile = 1), and how many there are. */
export function visibleStep(step: number, igStep: boolean): { current: number; total: number } {
  const shown = stepOrder(igStep).slice(1);
  return { current: Math.max(1, shown.indexOf(step) + 1), total: shown.length };
}

export function useOnboardingState() {
  const [step, setStep] = useState(() => readPersistedState().step);
  const [data, setData] = useState<OnboardingData>(() => readPersistedState().data);
  // Next / Back follow this sign-up's order; read through a ref so the
  // callbacks stay stable.
  const orderRef = useRef(stepOrder(data.igStep));
  orderRef.current = stepOrder(data.igStep);

  // Save to localStorage on changes
  useEffect(() => {
    try {
      const toSave = {
        step,
        data: {
          ...data,
          avatarFile: null, // Don't save File object
        },
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch (e) {
      console.warn("Failed to save onboarding state:", e);
    }
  }, [step, data]);

  const updateData = useCallback((updates: Partial<OnboardingData>) => {
    setData((prev) => ({ ...prev, ...updates }));
  }, []);

  // Onboarding flow has 3 steps: sign-in, profile, connect bank — plus
  // Connect Instagram before the bank when the sign-up has it (build 54). (Phase C
  // removed the price step; build 41 removed the access-code interstitial —
  // nobody is asked for a code any more, a new account is created unactivated
  // and waits for the founder's Approve; 1.5.1 #1 folded the second profile
  // screen into the first.)
  const nextStep = useCallback(() => {
    setStep((s) => {
      const order = orderRef.current;
      const i = order.indexOf(s);
      return i < 0 ? 3 : order[Math.min(i + 1, order.length - 1)];
    });
  }, []);

  const prevStep = useCallback(() => {
    setStep((s) => {
      const order = orderRef.current;
      const i = order.indexOf(s);
      return i < 0 ? 2 : order[Math.max(i - 1, 0)];
    });
  }, []);

  // `withIgStep`: the caller switches the Instagram step on in the same tick
  // (updateData hasn't flushed into the order yet).
  const goToStep = useCallback((s: number, withIgStep?: boolean) => {
    if (withIgStep) orderRef.current = stepOrder(true);
    setStep(orderRef.current.includes(s) ? s : 3);
  }, []);

  const clearState = useCallback(() => {
    setStep(1);
    setData(initialState);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Wipe ONLY the persisted onboarding data, leaving the in-memory step/data
  // untouched. Used right before the external Stripe redirect: we want stale
  // onboarding state gone from localStorage, but resetting the React step here
  // (as clearState does) would flash the page back to Step 1 for the second or
  // two the browser takes to load the Stripe URL. The page is about to unload,
  // so the in-memory state no longer matters — only localStorage does.
  const clearPersistedStorage = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // The website is about to leave for Instagram (a full page load): the
  // sign-up so far — name, photo, this step — must be on disk now. The save
  // above can fail on a big photo (the browser's storage is small), and then
  // NOTHING is saved, so the photo is shrunk first; if even that doesn't fit,
  // everything else is saved without it.
  const latest = useRef({ step, data });
  latest.current = { step, data };
  // `over`: what this same tick just learned (the claim), not yet in `data`.
  const saveBeforeLeaving = useCallback(async (over: Partial<OnboardingData> = {}) => {
    const s = latest.current.step;
    const d = { ...latest.current.data, ...over };
    const write = (photo: string | null) => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ step: s, data: { ...d, avatarFile: null, avatarPreview: photo } }));
        return true;
      } catch {
        return false;
      }
    };
    if (write(d.avatarPreview)) return;
    if (d.avatarPreview && write(await shrinkPhoto(d.avatarPreview))) return;
    write(null);
  }, []);

  return {
    step,
    data,
    updateData,
    nextStep,
    prevStep,
    goToStep,
    setStep,
    clearState,
    clearPersistedStorage,
    saveBeforeLeaving,
  };
}

// A cropped photo as a 512px PNG (the size it shows at, with room to spare).
// Resolves the original when it can't be drawn.
function shrinkPhoto(dataUrl: string, max = 512): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d")?.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}
